import { FC, memo } from 'react'
import { H2, H3 } from '@/components/atoms/typography'
import styled, { css } from 'styled-components'
import ProductDetailManufacturer from './productDetailManufacturer'
import ProductDetailTitle from './productDetailTitle'
import { Box, Flex } from '@/components/atoms/Grid'
import { convertHTMLStringToDOM } from '@/utils/convertHTMLStringToDOM'
import useTranslation from 'next-translate/useTranslation'
import { ArrowUpIconDefault } from '@/components/atoms/icons/ArrowUpIconDefault'
import { ArrowDownIconDefault } from '@/components/atoms/icons/ArrowDownIconDefault'
import {
  Desktop,
  MediaContextProvider,
  TabletOrMobile
} from '@/components/atoms/media'
import ProductDetailColorsChooser from '@/components/organisms/productDetailSideMenu/productDetailColorsChooser'
import { Product } from '@/network/graphql.g'

type ProductDetailNotAvailableSideMenuProps = {
  isExpanded: boolean
  handleChange: () => void
  unavailableProduct: Product
  setHoverColorVariant?: (hoverColorVariant: string) => void
  setHoverColorId?: (hoverColorId: string) => void
}

const ProductDetailNotAvailableSideMenu: React.FC<ProductDetailNotAvailableSideMenuProps> =
  ({
    isExpanded,
    handleChange,
    unavailableProduct,
    setHoverColorVariant,
    setHoverColorId
  }) => {
    const { t } = useTranslation('common')

    const {
      id,
      name,
      productBrandImage,
      productBrand,
      manufacturerUrl,
      description,
      colors
    } = unavailableProduct ?? {}

    const availableColors = colors?.filter((color) => color.id !== id)

    return (
      <>
        <ProductDetailManufacturer
          productBrandImage={productBrandImage}
          productBrand={productBrand}
          brandUrl={manufacturerUrl}
        />
        <Title>{t('ProductDetail.notAvailable')}</Title>
        <Flex onClick={handleChange}>
          {isExpanded && description?.description ? (
            <StyledUpIcon mb={2} />
          ) : (
            <StyledDownIcon mb={2} />
          )}
          <ProductDetailTitle
            name={name}
            productBrand={productBrand}
            brandUrl={manufacturerUrl}
          />
        </Flex>
        {isExpanded && description?.description && (
          <Box>{convertHTMLStringToDOM(description?.description)}</Box>
        )}
        {availableColors?.length > 0 && (
          <MediaContextProvider>
            <Desktop>
              <ProductDetailColorsChooser
                colors={availableColors}
                productBrand={productBrand}
                mainCategoryId={unavailableProduct?.mainCategory?.id}
                currentImage={unavailableProduct?.id}
                setHoverColorVariant={setHoverColorVariant}
                setHoverColorId={setHoverColorId}
                numberOfSlides={5.5}
                unavailableProduct={unavailableProduct}
              />
            </Desktop>
            <TabletOrMobile>
              <ProductDetailColorsChooser
                colors={availableColors}
                productBrand={productBrand}
                mainCategoryId={unavailableProduct?.mainCategory?.id}
                currentImage={unavailableProduct?.id}
                setHoverColorVariant={setHoverColorVariant}
                setHoverColorId={setHoverColorId}
                numberOfSlides={4.5}
                unavailableProduct={unavailableProduct}
              />
            </TabletOrMobile>
          </MediaContextProvider>
        )}
      </>
    )
  }

export default memo(ProductDetailNotAvailableSideMenu)

type ProductDetailNotAvailableTitleProps = {
  isModal: boolean
}

export const ProductDetailNotAvailableTitle: FC<ProductDetailNotAvailableTitleProps> =
  ({ isModal }) => {
    const { t } = useTranslation('common')
    return (
      <NotAvailableWrapper>
        {!isModal ? (
          <StyledH2>{t('ProductDetail.notAvailable')}</StyledH2>
        ) : (
          <StyledH3>{t('ProductDetail.notAvailable')}</StyledH3>
        )}
      </NotAvailableWrapper>
    )
  }

const NotAvailableWrapper = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  align-items: center;
  justify-content: center;
`

const BaseHeader = css`
  color: ${({ theme }) => theme.colors.background};
  background-color: ${({ theme }) => theme.colors.onBackground};
  height: fit-content;
  text-transform: uppercase;
`

const StyledH3 = styled(H3)`
  padding: 8px;
  ${BaseHeader}
`

const StyledH2 = styled(H2)`
  padding: 8px;
  ${BaseHeader}
`

const Title = styled(H2)`
  text-transform: uppercase;
`

const StyledUpIcon = styled(ArrowUpIconDefault)`
  padding-right: 5px;
  cursor: pointer;
`

const StyledDownIcon = styled(ArrowDownIconDefault)`
  padding-right: 5px;
  cursor: pointer;
`
